import store from "../../../../store";
import { LocalVideoTrack, createLocalVideoTrack, createLocalAudioTrack } from "twilio-video";
import { getMediaOptions, getCurrentAudioOutputDeviceId, setAudioOutputDeviceAsCurrent } from "./devices";
import { detachRemoteTrack } from "./Room/remoteParticipants";
import { registerMediaStateChange } from "./timestamps";
import { detachLocalTrack } from "./Room/localParticipant";
import roomAPIService from "../../roomAPIService";
import sessionSocket from "../../../../socket/sessionSocket";

/* Device types */
const CAMERA = "camera";
const MICROPHONE = "microphone";

/* Media state change constants */
const MIC_ON = "mic_on";
const MIC_OFF = "mic_off";
const CAM_ON = "cam_on";
const CAM_OFF = "cam_off";
const SCREEN_ON = "screen_on";
const SCREEN_OFF = "screen_off";

/** @note always use the real source from localParticipant to check if audio actually enabled or disabled, rather than Vuex */
export async function unmuteParticipant() {
  const localAudioTrack = store.state.room.localAudioTrack;
  if (!localAudioTrack || !localAudioTrack.isEnabled) {
    await publishNewTrackWithPreferences(MICROPHONE);
  }
}

export async function toggleAudio() {
  /** We use live status from the room itself, rather than stored version in central state. */

  if (isAnyAudioTrackEnabled()) {
    /* We completely remove the track from the DOM because in some odd edge cases 
     people's tracks were supposedly disabled yet their partner could still hear them. */
    await stopAndUnpublishAudio();
  } else {
    await publishNewTrackWithPreferences(MICROPHONE);

    /** If icon shows off but this just ran, something is wrong */
    const isMicrophoneOn = store.state.mediastates.userMediaSettings.microphoneOn;

    /** If mic icon is off, then ensure audio is also off */
    if (isMicrophoneOn === false) {
      roomAPIService.logSessionError({
        message: "Microphone icon is off but audio is still working.",
        data: { room: store.state.room.room, vuexRoom: store.state.room },
      });
      /** Do not use generic unpublish because it will send
       * API requests to server to register timestamp */
      await stopAndUnpublishAudio();
    }
  }
}

export async function toggleVideo() {
  const localVideoTrack = store.state.room.localVideoTrack;

  if (localVideoTrack && localVideoTrack.isEnabled) {
    await turnOffPictureInPicture(); /* if user turns camera off, ensure they cannot see the other person from picture-in-picture */
    await stopAndUnpublishVideo();
  } else {
    await publishNewTrackWithPreferences(CAMERA);
  }
}

/**
 * @NB DO NOT UNPACK THIS INTO A FURTHER ABSTRACTION, SUCH AS REMOVING THE DUPLICATION
 * OF THE CODE FOR CAMERA AND MICROPHONE. THIS IS BECAUSE WE MUST BE ABSOLUTELY SURE
 * THE VIDEO/AUDIO PUBLISHING WAS ACTUALLY SUCCESSFUL, BEFORE WE UPDATE UI OR REGISTER
 * THE TIMESTAMP.
 */
async function publishNewTrackWithPreferences(deviceType) {
  validateRoom();
  const room = store.state.room.room;
  const localParticipant = room.localParticipant;

  /* Get media options based on preferences and device availability for mic and cam */
  let { audioOptions, videoOptions } = await getMediaOptions();

  if (deviceType == CAMERA) {
    const settingData = { settingState: true, settingToToggle: "cameraOn" };
    const newTrack = await createLocalVideoTrack(videoOptions);
    await localParticipant.publishTrack(newTrack);
    store.dispatch("room/setLocalVideoTrack", newTrack);
    store.dispatch("mediastates/setSpecificMediaSettingState", settingData);
    /* Register media change timestamp */
    await registerMediaStateChange(CAM_ON);
  } else if (deviceType == MICROPHONE) {
    const settingData = { settingState: true, settingToToggle: "microphoneOn" };
    const newTrack = await createLocalAudioTrack(audioOptions);
    await localParticipant.publishTrack(newTrack);
    store.dispatch("room/setLocalAudioTrack", newTrack);
    store.dispatch("mediastates/setSpecificMediaSettingState", settingData);
    /* Register media change timestamp */
    await registerMediaStateChange(MIC_ON);
  }
}

export async function stopAndUnpublishVideo() {
  validateRoom();
  const room = store.state.room.room;
  room.localParticipant.videoTracks.forEach(async (publication) => {
    /** Skip if track name is 'screen', e.g. screenshare, handled separately */
    if (publication.track.name !== "screen") {
      publication.track.stop();
      publication.unpublish();
      detachLocalTrack(publication.track);
      const settingData = { settingState: false, settingToToggle: "cameraOn" };
      store.dispatch("mediastates/setSpecificMediaSettingState", settingData);
      store.dispatch("room/setLocalVideoTrack", null);

      /* Register media change timestamp */
      await registerMediaStateChange(CAM_OFF);
    }
  });
}

export async function stopAndUnpublishAudio() {
  const isTurningOffAudio = true;
  validateRoom(isTurningOffAudio);
  const room = store.state.room.room;

  let trackWasSuccessfullyDisabled = false;
  let tracksDisabled = 0;
  let totalTracks = room.localParticipant.audioTracks.size;

  room.localParticipant.audioTracks.forEach(async (audioTrackPublication) => {
    const audioTrack = audioTrackPublication.track;
    if (audioTrack) {
      audioTrack.disable();
      audioTrack.stop();
      audioTrackPublication.unpublish();
      detachLocalTrack(audioTrack);
      tracksDisabled++;
      trackWasSuccessfullyDisabled = true;
    } else {
      await roomAPIService.logSessionError({
        message: "AudioTrack Missing when trying to stop and unpublish",
        data: {
          room,
          vuexRoom: store.state.room,
        },
      });
    }
  });

  /** Assure no audio track is enabled before toggling UI state. This is done
   * as the LAST step to ensure that somehow the UI of the mic is not seemingly
   * turned off, when the mic did not actually get turned off. */
  if (!isAnyAudioTrackEnabled()) {
    const settingData = { settingState: false, settingToToggle: "microphoneOn" };
    store.dispatch("mediastates/setSpecificMediaSettingState", settingData);
    store.dispatch("room/setLocalAudioTrack", null);

    /* Register media change timestamp */
    await registerMediaStateChange(MIC_OFF);
  } else {
    /** Otherwise something went wrong. Log the error and notify the user. */
    await roomAPIService.logSessionError({
      message: "AudioTrack still enabled after trying to stop and unpublish",
      data: {
        room,
        vuexRoom: store.state.room,
      },
    });
    alert(
      "Something went wrong when trying to turn off your microphone. Please refresh and try again. If the problem persists, please contact us at support@cofocus.one. We apologize for the inconvenience."
    );
  }

  if (!trackWasSuccessfullyDisabled || tracksDisabled > 1 || totalTracks > 1) {
    await roomAPIService.logSessionError({
      message: "AudioTrack was not successfully disabled when trying to stop and unpublish",
      data: {
        room,
        vuexRoom: store.state.room,
        tracksDisabledNr: tracksDisabled,
        totalTracksNr: totalTracks,
      },
    });
  }
}

export async function republishMediaWithNewDevice(deviceType) {
  if (deviceType == CAMERA) {
    await stopAndUnpublishVideo();
  } else if (deviceType == MICROPHONE) {
    await stopAndUnpublishAudio();
  }
  await publishNewTrackWithPreferences(deviceType);
}

/* https://github.com/twilio/twilio-video.js/issues/762 */
export async function applyNewQualityPreferences() {
  const localParticipant = store.state.room.localParticipantData;
  const videoTrackPublication = [...localParticipant.videoTracks.values()][0];
  if (!videoTrackPublication) {
    console.warn("Video track not found.");
  } else {
    /* Get media options based on preferences and device availability for mic and cam */
    let videoOptions = await getMediaOptions(false, true);
    videoTrackPublication._signaling._trackTransceiver.track.applyConstraints(videoOptions).catch((e) => {
      console.error("Error while applying capture constraints:", e.message);
    });
  }
}

export async function changeAudioOutput() {
  /* Get new speaker preference device id */
  const newAudioOutputDeviceId = await getCurrentAudioOutputDeviceId();

  /* Remove current one from DOM */
  const remoteAudioTrack = store.state.room.remoteAudioTrack;
  if (remoteAudioTrack) {
    detachRemoteTrack(remoteAudioTrack);
    /* Set track to DOM outputted through new device */
    const audioElement = remoteAudioTrack.attach();
    await audioElement.setSinkId(newAudioOutputDeviceId);
    const container = document.getElementById("remote-video");
    container.appendChild(audioElement);
    store.dispatch("room/setRemoteAudioTrack", remoteAudioTrack);
  }
  await setAudioOutputDeviceAsCurrent(newAudioOutputDeviceId);
}

export async function toggleScreenshare() {
  let room = store.state.room.room;
  let screenBeingShared = store.state.mediastates.userMediaSettings.screenBeingShared;
  let localParticipantScreenData = store.state.room.localParticipantScreenData;

  let settingData = { settingToToggle: "screenBeingShared" };

  if (screenBeingShared) {
    // Stop capturing your screen.
    room.localParticipant.unpublishTrack(localParticipantScreenData);
    localParticipantScreenData.stop();
    localParticipantScreenData.detach().forEach((detachedElement) => {
      detachedElement.remove();
    });
    store.dispatch("room/setLocalParticipantScreenData", null);
    settingData.settingState = false;
    store.dispatch("mediastates/setSpecificMediaSettingState", settingData);
    await registerMediaStateChange(SCREEN_OFF);
  } else {
    try {
      let screenTrack = await createScreenshareTrack(720, 1280);
      await store.dispatch("room/setLocalParticipantScreenData", screenTrack);

      let screenshareContainer = document.getElementById("localScreenshare");
      screenshareContainer.appendChild(screenTrack.attach());
      room.localParticipant.publishTrack(screenTrack);
      settingData.settingState = true;
      store.dispatch("mediastates/setSpecificMediaSettingState", settingData);
      screenTrack.on("stopped", () => {
        toggleScreenshare();
      });
      await registerMediaStateChange(SCREEN_ON);
    } catch (error) {
      alert(error.message);
    }
  }
}
async function createScreenshareTrack(height, width) {
  if (typeof navigator === "undefined" || !navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
    alert("Screen sharing is not supported or allowed in this browser. If you think this is a mistake, please contact us at support@cofocus.one.");
    throw new Error("getDisplayMedia is not supported");
  }
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: {
      height: height,
      width: width,
    },
  });
  const track = stream.getVideoTracks()[0];
  return new LocalVideoTrack(track, { name: "screen" });
}

export async function togglePictureInPicture() {
  /** If own video is not turned off, do not allow Picture In Picture */
  const localVideoTrack = store.state.room.localVideoTrack;
  if (!store.state.mediastates.userMediaSettings.cameraOn || !localVideoTrack || !localVideoTrack.isEnabled) {
    return;
  }

  /* Find remote video */
  const remoteVideoContainer = document.getElementById("remote-video");
  const remoteVideo = remoteVideoContainer.getElementsByTagName("video")[0];

  let pictureInPictureEnabled = store.state.mediastates.userMediaSettings.pictureInPictureEnabled;

  if (pictureInPictureEnabled && document.pictureInPictureElement) {
    document.exitPictureInPicture().catch((error) => {
      console.log(error);
      return alert("Failed to quit Picture In Picture view. Please refresh and feel free to contact us!");
    });
  } else {
    if (remoteVideo) {
      remoteVideo.requestPictureInPicture().catch((error) => {
        console.log(error);
        return alert("Failed to toggle Picture In Picture view. Refresh and try again, or feel free to contact us!");
      });
    }
  }
}

async function turnOffPictureInPicture() {
  let pictureInPictureEnabled = store.state.mediastates.userMediaSettings.pictureInPictureEnabled;

  if (pictureInPictureEnabled && document.pictureInPictureElement) {
    document.exitPictureInPicture().catch((error) => {
      console.log(error);
      return alert("Failed to quit Picture In Picture view. Please refresh and feel free to contact us!");
    });
  }
}

export function initPictureInPictureListening() {
  /* Find remote video */
  const remoteVideoContainer = document.getElementById("remote-video");
  const remoteVideo = remoteVideoContainer.getElementsByTagName("video")[0];

  let settingData = { settingToToggle: "pictureInPictureEnabled", settingState: false };

  remoteVideo.addEventListener("enterpictureinpicture", () => {
    settingData.settingState = true;
    store.dispatch("mediastates/setSpecificMediaSettingState", settingData);
  });

  remoteVideo.addEventListener("leavepictureinpicture", () => {
    settingData.settingState = false;
    store.dispatch("mediastates/setSpecificMediaSettingState", settingData);
  });
}

/** ========== MEDIA HELPERS ========== */

function validateRoom(isTurningOffAudio = false) {
  const room = store.state.room.room;
  if (!room) {
    roomAPIService.logSessionError({
      message: "Room not found when trying to validate room.",
      data: { room, vuexRoom: store.state.room },
    });
  }
  if (!room.localParticipant) {
    roomAPIService.logSessionError({
      message: "Local participant not found when trying to validate room.",
      data: { room, vuexRoom: store.state.room },
    });
  }
  if (isTurningOffAudio) {
    if (!room.localParticipant.audioTracks || !room.localParticipant.audioTracks.size) {
      roomAPIService.logSessionError({
        message: "Local participant audio tracks not found when trying to validate room.",
        data: { room, vuexRoom: store.state.room },
      });
    }
  }
}

export function isAnyAudioTrackEnabled() {
  let isLocalAudioTrackEnabled = false;
  const room = store.state.room.room;
  if (!room) {
    /** Check if audio exist sin Vuex room and attempt use that one */
    const localAudioTrack = store.state.room.localAudioTrack;
    if (localAudioTrack && localAudioTrack.isEnabled) {
      isLocalAudioTrackEnabled = true;
    }
  } else {
    /** The user is only supposed to have one active audio track, as such, if any exist at all,
     * this is an indication it is enabled. */
    room.localParticipant.audioTracks.forEach((audioTrackPublication) => {
      const localAudioTrack = audioTrackPublication.track;
      if (localAudioTrack && localAudioTrack.isEnabled) {
        isLocalAudioTrackEnabled = true;
      }
    });
  }

  return isLocalAudioTrackEnabled;
}

export function isAnyVideoTrackEnabled() {
  let isLocalVideoTrackEnabled = false;
  const room = store.state.room.room;
  if (!room) {
    /** Check if video exists in Vuex room and attempt use that one */
    const localVideoTrack = store.state.room.localVideoTrack;
    if (localVideoTrack && localVideoTrack.isEnabled) {
      isLocalVideoTrackEnabled = true;
    }
  } else {
    /** The user is only supposed to have one active video track, as such, if any exist at all,
     * this is an indication it is enabled. */
    room.localParticipant.videoTracks.forEach((videoTrackPublication) => {
      const localVideoTrack = videoTrackPublication.track;
      if (localVideoTrack && localVideoTrack.isEnabled) {
        isLocalVideoTrackEnabled = true;
      }
    });
  }
  return isLocalVideoTrackEnabled;
}

/** In some cases, there is some race condition where the states like BRB are not captured
 * properly. This is to ensure these states are always in sync.
 */
let timeoutID = null;
export async function syncMediaStates() {
  const checkInterval = 3000; // Check every 2.5 seconds (2500 ms)

  const requestPartnerStates = async () => {
    await sessionSocket.requestPartnerState();

    // Schedule the next check based on the actual time passed
    const nextCheck = new Date().getTime() + checkInterval;
    timeoutID = setTimeout(async () => {
      await requestPartnerStates();
    }, nextCheck - new Date().getTime());
  };

  // Start the first check
  await requestPartnerStates();
}

// Function to stop the syncing process
export function stopSyncingMediaStates() {
  clearTimeout(timeoutID);
}

export default {
  toggleAudio,
  unmuteParticipant,
  toggleVideo,
  toggleScreenshare,
  togglePictureInPicture,
  initPictureInPictureListening,
  stopAndUnpublishVideo,
  stopAndUnpublishAudio,
  isAnyAudioTrackEnabled,
  isAnyVideoTrackEnabled,
  syncMediaStates,
  stopSyncingMediaStates,
};
